/* eslint-disable max-lines */
import _, { cloneDeep } from 'lodash'
import store from '@ps/store'
import {
  AstConditionItem,
  AstActionItem,
  AstEventItem,
} from '@ps/bridge/qc/scripts/editor'
import {
  astItemFactory,
  isDragCondition,
} from '@ps/store/workbench/visual-program/utils'
import qc from 'qc'
import iframe from '@ps/bridge/iframe/iframe'
import { getVpAstFromSceneNode } from '@ps/bridge/qc/vp'
import {
  makeCondition,
  getEventItemGroupById,
} from '@ps/store/workbench/visual-program/utils'
import { language } from '@/common/storage'
import {
  DragConditionType,
  DrawLineConditionType,
  EraseConditionType,
} from '@ps/bridge/qc/vp/config'
import nodePool from '@ps/bridge/qc/node-pool'
import { generateUUID } from '@ps/utils/game'
import { cookCondition } from '@ps/bridge/qc/scripts/editor'

type AddEventValue = {
  newValue: { condition: AstConditionItem; sceneId?: string }
  oldValue: { index: number; sceneId?: string }
}
type RemoveEventValue = {
  newValue: { eventId: string; sceneId?: string }
  oldValue: { sceneId?: string }
}
type EventEditActionItem = {
  astIndex: number
  actionIndex: number
  action: AstActionItem
  astItem: AstEventItem
}
type EventEditAction = {
  newValue: EventEditActionItem
  oldValue: EventEditActionItem
}
type EventAddAction = {
  newValue: { astIndex: number; actionIndex: number; sceneId?: string }
  oldValue: {
    actionIndex: number
    action: AstActionItem
    astIndex: number
    sceneId?: string
  }
}
type AddEventAction = {
  newValue: { astIndex: number; action: AstActionItem; sceneId?: string }
  oldValue: { astIndex: number; sceneId?: string }
  response?: { actionIndex: number }
}
type BatchDelActionItem = {
  actionId: string
  eventId: string
}
type BatchDelAction = {
  newValue: { deleteActions: BatchDelActionItem[]; sceneId?: string }
  oldValue: { deleteActions: BatchDelActionItem[]; sceneId?: string }
}
type BatchDelItem = {
  astIndex: number
  actionIndex: number
  action: AstActionItem
  astItem: AstEventItem
}
type ChangeEventItem = {
  astIndex: number
  actionIndex: number
  oldAction: AstActionItem
  newAction: AstActionItem
}

type EventActionFilter = {
  newValue: { filterCategories: string[] }
  oldValue: { filterCategories: string[] }
}

type ChangeEventToOtherSceneAction = {
  newValue: { editActions: ChangeEventItem[]; sceneId: string }
  oldValue: { editActions: ChangeEventItem[]; sceneId: string }
}

type AddCompareConditionValue = {
  newValue: { condition: AstConditionItem; eventId: string; sceneId?: string }
  oldValue: { eventId: string; sceneId?: string }
}
type EditCompareConditionValue = {
  newValue: {
    condition: AstConditionItem
    compareIndex: number
    eventId: string
  }
  oldValue: {
    condition: AstConditionItem
    compareIndex: number
    eventId: string
  }
}
type deleteCompareConditionValue = {
  newValue: {
    compareIndex: number
    eventId: string
    sceneId?: string
  }
  oldValue: {
    compareIndex: number
    eventId: string
    sceneId?: string
  }
}
type deleteCompareConditionsValue = {
  newValue: {
    eventId: string
    sceneId?: string
  }
  oldValue: {
    sceneId?: string
  }
}
type editConditionsForTypeChangeValue = {
  newValue: {
    cmd: any
    eventId: string
    sceneId?: string
  }
  oldValue: {
    cmd: any
    eventId: string
    sceneId?: string
  }
}
type editEventNameValue = {
  newValue: {
    eventId: string
    eventName: string
  }
  oldValue: {
    eventId: string
    eventName: string
  }
}

function getUpdateAst(sceneId: string | undefined) {
  const currentSceneNode =
    store.state.workspace.game.sceneManager.currentSceneNode
  const noAstUpdate = sceneId !== undefined && sceneId !== currentSceneNode.uuid // 是否需要更新store中的ast变量，当是当前场景时，需要更新，不传也表示当前场景
  const sceneNode: qc.Node = sceneId
    ? iframe.gamePlay.children.find((node) => node.uuid === sceneId)
    : currentSceneNode
  return { noAstUpdate, sceneNode }
}

function vpAddEventItem({
  condition,
  sceneNode,
}: {
  condition: AstConditionItem
  sceneNode: qc.Node
}) {
  const ast = getVpAstFromSceneNode(sceneNode) as AstEventItem[]
  const astItem = astItemFactory(condition)
  ast.push(astItem)
  return ast
}
function vpAddEventItemByIndex({
  sceneNode,
  index,
  astItem,
}: {
  astItem: AstEventItem
  sceneNode: qc.Node
  index: number
}) {
  const vpAst = getVpAstFromSceneNode(sceneNode) as AstEventItem[]
  vpAst.splice(index, 0, astItem)
  return vpAst
}
function vpAddAction({
  sceneNode,
  astIndex,
  action,
  index,
}: {
  sceneNode: qc.Node
  astIndex: number
  action: AstActionItem
  index?: number
}) {
  const ast = getVpAstFromSceneNode(sceneNode) as AstEventItem[]
  if (!Array.isArray(ast[astIndex].action)) ast[astIndex].action = []
  if (typeof index === 'undefined') {
    ast[astIndex].action.push(action)
  } else {
    ast[astIndex].action.splice(index, 0, action)
  }
  return ast
}
function vpDeleteAction({
  sceneNode,
  astIndex,
  actionIndex,
}: {
  sceneNode: qc.Node
  astIndex: number
  actionIndex: number
}) {
  const vpAst = getVpAstFromSceneNode(sceneNode) as AstEventItem[]
  const astItem = vpAst[astIndex]
  const delAction = astItem.action.splice(actionIndex, 1)
  return { vpAst, delAction: cloneDeep(delAction[0]) }
}

function vpDeleteActionById({
  ast,
  astItemId,
  actionId,
}: {
  ast: AstEventItem[]
  astItemId: string
  actionId: string
}) {
  // const vpAst = getVpAstFromSceneNode(sceneNode) as AstEventItem[]
  const astIndex = ast.findIndex((item) => item.id === astItemId)
  const astItem = ast[astIndex]
  const actionIndex = astItem.action.findIndex((item) => item.uuid === actionId)
  const delAction = astItem.action.splice(actionIndex, 1)
  return { ast, delAction: cloneDeep(delAction[0]) }
}

function vpDeleteEventItem({
  astIndex,
  sceneNode,
}: {
  astIndex: number
  sceneNode: qc.Node
}): { ast: AstEventItem[]; delAstItem: AstEventItem } {
  const ast = getVpAstFromSceneNode(sceneNode) as AstEventItem[]
  const delAstItem = ast.splice(astIndex, 1)
  return { ast, delAstItem: cloneDeep(delAstItem[0]) }
}

function vpDeleteEventItems({
  eventIds,
  sceneNode,
}: {
  eventIds: string[]
  sceneNode: qc.Node
}): { ast: AstEventItem[] } {
  const ast = getVpAstFromSceneNode(sceneNode) as AstEventItem[]
  const ansAst = ast.filter((item) => !eventIds.includes(item.id))
  return { ast: ansAst }
}
function vpDeleteEventItemById({
  astItemId,
  ast,
}: {
  ast: AstEventItem[]
  astItemId: string
}): { ast: AstEventItem[]; delAstItem: AstEventItem } {
  const index = ast.findIndex((item) => item.id === astItemId)
  const delAstItem = ast.splice(index, 1)
  return { ast, delAstItem: cloneDeep(delAstItem[0]) }
}

function vpBatchDelActions({
  deleteActions,
  sceneNode,
}: {
  deleteActions: BatchDelActionItem[]
  sceneNode: qc.Node
}) {
  const ast = getVpAstFromSceneNode(sceneNode) as AstEventItem[]
  const deleteIds = deleteActions.map((dAction) => {
    const astIndex = ast.findIndex((item) => item.id === dAction.eventId)
    const astItem = ast[astIndex]
    const actionIndex = astItem.action.findIndex(
      (item) => item.uuid === dAction.actionId,
    )
    return {
      astIndex,
      actionIndex,
      action: cloneDeep(astItem.action[actionIndex]),
      astItem: cloneDeep(astItem),
    }
  })
  let vpAst: AstEventItem[] = []
  deleteIds.forEach((item) => {
    const { astItem, action } = item
    if (astItem.action.length === 1) {
      vpAst = vpDeleteEventItemById({ astItemId: astItem.id, ast }).ast
    } else {
      vpAst = vpDeleteActionById({
        astItemId: astItem.id,
        actionId: action.uuid,
        ast,
      }).ast
    }
  })
  return { vpAst, deleteIds }
}

function vpEditAction({
  astIndex,
  actionIndex,
  action,
  sceneNode,
}: {
  astIndex: number
  actionIndex: number
  action: AstActionItem
  sceneNode: qc.Node
}) {
  const ast = getVpAstFromSceneNode(sceneNode) as AstEventItem[]
  const astItem = ast[astIndex]
  astItem.action.splice(actionIndex, 1, action)
  return ast
}

function vpAddCompareItem({
  condition,
  sceneNode,
  eventId,
}: {
  condition: AstConditionItem
  sceneNode: qc.Node
  eventId: string
}) {
  const ast = getVpAstFromSceneNode(sceneNode) as AstEventItem[]
  const event = ast.find((item) => item.id === eventId)
  if (event) {
    if (
      event.condition[0].type === 'compare' &&
      typeof event.condition[0].params === 'undefined'
    ) {
      event.condition[0] = condition
    } else {
      event.condition.push(condition)
    }
  }
  return ast
}

function vpAddCompareConditionByIndex({
  condition,
  sceneNode,
  eventId,
  compareIndex,
}: {
  condition: AstConditionItem
  sceneNode: qc.Node
  eventId: string
  compareIndex: number
}) {
  const ast = getVpAstFromSceneNode(sceneNode) as AstEventItem[]
  const event = ast.find((item) => item.id === eventId)
  if (event) {
    if (
      event.condition.length === 1 &&
      event.condition[0].type === 'compare' &&
      typeof event.condition[0].params === 'undefined' &&
      compareIndex === 0
    ) {
      event.condition[0] = condition
    } else {
      event.condition.splice(compareIndex, 0, condition)
    }
  }
  return ast
}

function vpDeleteCompareItem({
  eventId,
  conditionIndex,
  sceneNode,
}: {
  eventId: string
  conditionIndex: number
  sceneNode: qc.Node
}) {
  let ast = getVpAstFromSceneNode(sceneNode) as AstEventItem[]
  const event = ast.find((item) => item.id === eventId)
  if (!event) {
    console.error('找不到事件')
    return
  }
  event.condition.splice(conditionIndex, 1)
  // 条件数组中没有compare事件类型的就清空该eventItem
  ast = ast.filter((item) => {
    return item.condition.some((con) => con.type === 'compare')
  })
  return ast
}

function vpDeleteCompareItemByIndex({
  eventId,
  compareIndex,
  sceneNode,
}: {
  eventId: string
  compareIndex: number
  sceneNode: qc.Node
}) {
  const ast = getVpAstFromSceneNode(sceneNode) as AstEventItem[]
  const event = ast.find((item) => item.id === eventId)
  if (!event) {
    throw new Error(`找不到条件判断${eventId}`)
  }
  let delCondition
  if (event.condition.length === 1 && compareIndex === 0) {
    delCondition = cloneDeep(event.condition[0])
    delete event.condition[0].params
  } else {
    delCondition = event.condition.splice(compareIndex, 1)
    delCondition = cloneDeep(delCondition[0])
  }
  return { ast, delCondition }
}

function vpDeleteCompareConditions({
  eventId,
  sceneNode,
}: {
  eventId: string
  sceneNode: qc.Node
}) {
  let ast = getVpAstFromSceneNode(sceneNode) as AstEventItem[]
  ast = ast.filter((item) => item.id !== eventId)
  return { ast }
}

function vpAddCompareConditions({
  condition,
  sceneNode,
  eventId,
}: {
  condition: AstConditionItem[]
  sceneNode: qc.Node
  eventId: string
}) {
  const ast = getVpAstFromSceneNode(sceneNode) as AstEventItem[]
  const event = ast.find((item) => item.id === eventId)
  if (event) {
    if (
      event.condition.length === 1 &&
      event.condition[0].type === 'compare' &&
      typeof event.condition[0].params === 'undefined'
    ) {
      event.condition = condition
    } else {
      event.condition.push(...condition)
    }
  }
  return ast
}
// 兜底统一处理drager.dragBound数据
function confirmDragBound(ast: AstEventItem[]) {
  ast.forEach((eventItem: AstEventItem) => {
    if (
      eventItem.condition &&
      eventItem.condition[0]?.content === DragConditionType.VPDRAG_END &&
      eventItem.condition[0]?.node
    ) {
      const node = nodePool.find(eventItem.condition[0].node)
      if (node) {
        const drager = node.getScript('ps.Drager')
        if (eventItem.condition[0].ext?.dragBound) {
          drager.dragBound = nodePool.find(
            eventItem.condition[0].ext?.dragBound,
          )
        } else {
          drager.dragBound = null
        }
      }
    }
  })
}

export function getAddEvent(value: AddEventValue) {
  return {
    execute: ({ newValue }: AddEventValue) => {
      const { condition, sceneId } = newValue
      const { sceneNode, noAstUpdate } = getUpdateAst(sceneId)
      const vpAst = vpAddEventItem({ condition, sceneNode })
      store.dispatch('workspace/game/visualProgram/saveAST', {
        ast: vpAst,
        sceneNode,
        noAstUpdate,
      })
    },
    // eslint-disable-next-line
    undo: async ({ oldValue }: AddEventValue) => {
      const { index, sceneId } = oldValue
      const { sceneNode, noAstUpdate } = getUpdateAst(sceneId)
      const { ast: vpAst } = vpDeleteEventItem({ astIndex: index, sceneNode })
      store.dispatch('workspace/game/visualProgram/saveAST', {
        ast: vpAst,
        sceneNode,
        noAstUpdate,
      })
    },
    value,
    methodName: 'getAddEventAction',
  }
}

export function getRemoveEvent(value: RemoveEventValue) {
  return {
    execute: async ({ newValue }: RemoveEventValue) => {
      const { eventId, sceneId } = newValue
      const { sceneNode, noAstUpdate } = getUpdateAst(sceneId)
      const sceneAst = getVpAstFromSceneNode(sceneNode) as AstEventItem[]
      const oriAst = cloneDeep(sceneAst)
      const eventAst = getEventItemGroupById(sceneAst, eventId, true)
      const ansEventAstId = eventAst.map((item) => item.id)
      eventAst
        .filter(
          (k) =>
            k?.condition[0]?.content === 'VPDRAG_END' ||
            k?.condition[0]?.content === 'VPUNDRAG_END',
        )
        .map((k) => k?.condition[0]?.ext?.dragTargetArea)
        .forEach((v) => {
          if (v) {
            // 是drag
            Object.values(v).forEach((uuids) =>
              uuids.forEach((uuid) => {
                const node = iframe.qcGame.nodePool.find(uuid)
                node && node.parent && node.parent.removeChild?.(node)
              }),
            )
          }
        })
      const dragEvents = sceneAst.filter(
        (item) =>
          isDragCondition(item?.condition[0]?.content) &&
          item.condition[0].node === eventAst[0]?.condition[0].node,
      )
      // 同节点下有多个drag事件就不需要删除拖拽范围节点
      if (dragEvents.length === 1) {
        eventAst
          .filter((k) => isDragCondition(k?.condition[0]?.content))
          .map((k) => k?.condition[0]?.ext?.dragBound)
          .forEach((uuid) => {
            if (uuid) {
              const node = iframe.qcGame.nodePool.find(uuid)
              node && node.parent && node.parent.removeChild?.(node)
            }
          })
        const node = iframe.qcGame.nodePool.find(
          dragEvents[0]?.condition[0]?.node,
        )
        if (node) {
          const drager = node.getScript('ps.Drager')
          if (drager) {
            drager.dragBound = null
          }
        }
      }

      const { ast } = vpDeleteEventItems({
        eventIds: ansEventAstId,
        sceneNode,
      })
      confirmDragBound(ast)
      await store.dispatch('workspace/game/visualProgram/saveAST', {
        ast,
        sceneNode,
        noAstUpdate,
      })
      return { oriAst }
    },
    undo: async (
      { oldValue }: RemoveEventValue,
      response: { oriAst: AstEventItem[] },
    ) => {
      const { sceneId } = oldValue
      const vpAst = (await response).oriAst
      const { sceneNode, noAstUpdate } = getUpdateAst(sceneId)
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      vpAst
        .filter(
          (k) =>
            k?.condition[0]?.content === 'VPDRAG_END' ||
            k?.condition[0]?.content === 'VPUNDRAG_END',
        )
        .map((k) => k?.condition[0]?.ext?.dragTargetArea)
        .forEach((v) => {
          if (v) {
            // 是drag
            Object.values(v).forEach((uuids) =>
              uuids.forEach((uuid) => {
                store.dispatch(
                  'workspace/game/recoverDragTargetAreaByUuid',
                  uuid,
                )
              }),
            )
          }
        })
      vpAst.forEach((item) => {
        if (item.condition[0] && isDragCondition(item.condition[0].content)) {
          if (item.condition[0].ext && item.condition[0].ext.dragBound) {
            const uuid = item.condition[0].ext.dragBound
            const node = nodePool.find(uuid)
            const dragerNode = nodePool.find(item.condition[0].node)
            if (node) {
              store.dispatch('workspace/game/recoverDragBoundByUuid', {
                uuid,
                dragerNode,
              })
            }
          }
        }
      })
      confirmDragBound(vpAst)
      store.dispatch('workspace/game/visualProgram/saveAST', {
        ast: vpAst,
        sceneNode,
        noAstUpdate,
      })
    },
    value,
    methodName: 'getRemoveEventAction',
  }
}

// 只可能在当前场景编辑，暂时不改
export function getEventEditAction(value: EventEditAction) {
  return {
    // eslint-disable-next-line
    execute: async ({ newValue }: EventEditAction) => {
      const { astIndex, actionIndex, action } = newValue
      store.commit('workspace/game/visualProgram/editAction', {
        astIndex,
        actionIndex,
        action,
      })
      await store.dispatch('workspace/game/visualProgram/saveAST')
    },
    // eslint-disable-next-line
    undo: async ({ oldValue }: EventEditAction) => {
      const { astIndex, actionIndex, action } = oldValue
      store.commit('workspace/game/visualProgram/editAction', {
        astIndex,
        actionIndex,
        action,
      })
      await store.dispatch('workspace/game/visualProgram/saveAST')
    },
    value,
    async: true,
    methodName: 'getEventEditAction',
  }
}

export function getEventAddActionHistory(value: AddEventAction) {
  return {
    // eslint-disable-next-line
    execute: async ({ newValue }: AddEventAction) => {
      const { astIndex, action, sceneId } = newValue
      const { sceneNode, noAstUpdate } = getUpdateAst(sceneId)
      const vpAst = vpAddAction({ sceneNode, astIndex, action })
      await store.dispatch('workspace/game/visualProgram/saveAST', {
        ast: vpAst,
        sceneNode,
        noAstUpdate,
      })
      const astItem = vpAst[astIndex]
      const actionIndex = astItem.action.length - 1
      return { actionIndex }
    },
    // eslint-disable-next-line
    undo: async (
      { oldValue }: AddEventAction,
      response: { actionIndex: number },
    ) => {
      const actionIndex = (await response).actionIndex
      const { astIndex, sceneId } = oldValue
      const { sceneNode, noAstUpdate } = getUpdateAst(sceneId)
      const { vpAst } = vpDeleteAction({ sceneNode, actionIndex, astIndex })
      await store.dispatch('workspace/game/visualProgram/saveAST', {
        ast: vpAst,
        sceneNode,
        noAstUpdate,
      })
    },
    value,
    async: true,
    methodName: 'getEventAddActionHistory',
  }
}

export function getEventRemoveActionHistory(value: EventAddAction) {
  return {
    // eslint-disable-next-line
    execute: async ({ newValue }: EventAddAction) => {
      const { astIndex, actionIndex, sceneId } = newValue
      const { sceneNode, noAstUpdate } = getUpdateAst(sceneId)
      const { vpAst, delAction } = vpDeleteAction({
        sceneNode,
        actionIndex,
        astIndex,
      })
      if (delAction.method === 'vpWaitTime') {
        const timerId = delAction.param!.timerId
        const vpTimer =
          iframe.UIRoot.getChild('gamePlay').getScript('ps.VPTimer')
        const timers = vpTimer.timers
        delete timers[timerId]
      }
      await store.dispatch('workspace/game/visualProgram/saveAST', {
        ast: vpAst,
        sceneNode,
        noAstUpdate,
      })
      return { delAction }
    },
    // eslint-disable-next-line
    undo: async (
      { oldValue }: EventAddAction,
      response: { delAction: AstActionItem },
    ) => {
      const { actionIndex, astIndex, sceneId } = oldValue
      const { sceneNode, noAstUpdate } = getUpdateAst(sceneId)
      const delAction = (await response).delAction
      if (delAction.method === 'vpWaitTime') {
        const timerId = delAction.param!.timerId
        const vpTimer =
          iframe.UIRoot.getChild('gamePlay').getScript('ps.VPTimer')
        const timers = vpTimer.timers
        timers[timerId] = null
      }
      const vpAst = vpAddAction({
        sceneNode,
        astIndex,
        index: actionIndex,
        action: delAction,
      })
      await store.dispatch('workspace/game/visualProgram/saveAST', {
        ast: vpAst,
        sceneNode,
        noAstUpdate,
      })
    },
    value,
    async: true,
    methodName: 'getEventRemoveActionHistory',
  }
}

export function getBatchDelActions(value: BatchDelAction) {
  return {
    // eslint-disable-next-line
    execute: async ({ newValue }: BatchDelAction) => {
      const { deleteActions, sceneId } = newValue
      const { sceneNode, noAstUpdate } = getUpdateAst(sceneId)
      const { deleteIds, vpAst } = vpBatchDelActions({
        deleteActions,
        sceneNode,
      })
      await store.dispatch('workspace/game/visualProgram/saveAST', {
        ast: vpAst,
        sceneNode,
        noAstUpdate,
      })
      await store.dispatch('workspace/storyboard/renderEdges')
      return { deleteIds }
    },
    // eslint-disable-next-line
    undo: async (
      { oldValue }: BatchDelAction,
      response: Promise<{ deleteIds: BatchDelItem[] }>,
    ) => {
      const deleteIds = (await response).deleteIds
      const { sceneId } = oldValue
      const { sceneNode, noAstUpdate } = getUpdateAst(sceneId)
      let vpAst: AstEventItem[] = []
      deleteIds.forEach((item) => {
        const { actionIndex, action, astIndex, astItem } = item
        if (astItem.action.length === 1) {
          vpAst = vpAddEventItemByIndex({ sceneNode, astItem, index: astIndex })
          store.dispatch('workspace/game/visualProgram/saveAST', {
            ast: vpAst,
            sceneNode,
            noAstUpdate,
          })
        } else {
          vpAst = vpAddAction({
            sceneNode,
            index: actionIndex,
            astIndex,
            action,
          })
          store.dispatch('workspace/game/visualProgram/saveAST', {
            ast: vpAst,
            sceneNode,
            noAstUpdate,
          })
        }
      })
      await store.dispatch('workspace/storyboard/renderEdges')
    },
    value,
    async: true,
    methodName: 'getBatchDelActions',
  }
}
export function getChangeEventActionFilter(value: EventActionFilter) {
  return {
    execute: ({ newValue }: EventActionFilter) => {
      store.commit('workspace/game/visualProgram/updateFilterCategories', {
        categories: newValue.filterCategories,
      })
    },
    undo: ({ oldValue }: EventActionFilter) => {
      store.commit('workspace/game/visualProgram/updateFilterCategories', {
        categories: oldValue.filterCategories,
      })
    },
    value,
    methodName: 'getChangeEventActionFilter',
  }
}

export function getChangeEventToOtherSceneAction(
  value: ChangeEventToOtherSceneAction,
) {
  return {
    // eslint-disable-next-line
    execute: async ({ newValue }: ChangeEventToOtherSceneAction) => {
      const { editActions, sceneId } = newValue
      const { sceneNode, noAstUpdate } = getUpdateAst(sceneId)
      let vpAst: AstEventItem[] = []
      editActions.forEach((item) => {
        const { astIndex, actionIndex, newAction: action } = item
        vpAst = vpEditAction({ astIndex, actionIndex, action, sceneNode })
        store.dispatch('workspace/game/visualProgram/saveAST', {
          ast: vpAst,
          sceneNode,
          noAstUpdate,
        })
      })
      await store.dispatch('workspace/storyboard/renderEdges')
    },
    // eslint-disable-next-line
    undo: async ({ oldValue }: ChangeEventToOtherSceneAction) => {
      const { editActions, sceneId } = oldValue
      const { sceneNode, noAstUpdate } = getUpdateAst(sceneId)
      let vpAst: AstEventItem[] = []
      editActions.forEach((item) => {
        const { astIndex, actionIndex, oldAction: action } = item
        vpAst = vpEditAction({ astIndex, actionIndex, action, sceneNode })
        store.dispatch('workspace/game/visualProgram/saveAST', {
          ast: vpAst,
          sceneNode,
          noAstUpdate,
        })
      })
      await store.dispatch('workspace/storyboard/renderEdges')
    },
    value,
    async: true,
    methodName: 'getChangeEventToOtherSceneAction',
  }
}

export function getAddCompareCondition(value: AddCompareConditionValue) {
  return {
    execute: async ({ newValue }: AddCompareConditionValue) => {
      const { condition, sceneId } = newValue
      let eventId = newValue.eventId
      const { sceneNode, noAstUpdate } = getUpdateAst(sceneId)
      const ast = getVpAstFromSceneNode(sceneNode) as AstEventItem[]
      const oriAst = cloneDeep(ast)
      // 没有eventId，需要新建一份eventId的eventItem
      if (eventId === null) {
        const node =
          store.state.workspace.game.activeNode ||
          store.state.workspace.game.sceneManager.currentSceneNode
        let initCondition
        let needAddEventItem = false
        if (store.state.workspace.globalVar.dialogConditionEventItemId) {
          const event = ast.find(
            (item) =>
              item.id ===
              store.state.workspace.globalVar.dialogConditionEventItemId,
          )
          if (!event) {
            console.error('找不到事件')
            return
          }
          if (event.condition[0].type === 'event') {
            const events = getEventItemGroupById(
              ast,
              store.state.workspace.globalVar.dialogConditionEventItemId,
              true,
            )
            const eventItemHasNoCompare = events.find((item) => {
              return item.condition.every((con) => con.type !== 'compare')
            })
            if (eventItemHasNoCompare) {
              // 非条件判断的手势条件就不用新建一个eventItem
              eventId = eventItemHasNoCompare.id
              needAddEventItem = false
            } else {
              initCondition = cloneDeep(event.condition[0])
              needAddEventItem = true
            }
          } else {
            if (event.condition[0].params) {
              initCondition = makeCondition({
                uuid: node.uuid,
                conf: {
                  type: 'compare',
                  label: '条件判断',
                  command: 'LOGIC',
                },
              })
              needAddEventItem = true
            } else {
              eventId = event.id
              needAddEventItem = false
            }
          }
        } else {
          initCondition = makeCondition({
            uuid: node.uuid,
            conf: {
              type: 'compare',
              label: '条件判断',
              command: 'LOGIC',
            },
          })
          needAddEventItem = true
        }
        if (needAddEventItem) {
          store.commit('workspace/game/visualProgram/addEventItem', {
            condition: initCondition,
          })
          eventId =
            store.state.workspace.game.visualProgram.ast[
              store.state.workspace.game.visualProgram.ast.length - 1
            ].id
          await store.dispatch('workspace/game/visualProgram/saveAST')
        }
      }
      const vpAst = vpAddCompareItem({ condition, sceneNode, eventId })
      store.dispatch('workspace/game/visualProgram/saveAST', {
        ast: vpAst,
        sceneNode,
        noAstUpdate,
      })
      const event = vpAst.find((item) => item.id === eventId)
      if (!event) {
        console.error('没有对应的事件')
        return
      }
      store.commit('workspace/globalVar/updateDialogConditionId', eventId)
      return { vpAst: oriAst }
    },
    // eslint-disable-next-line
    undo: async (
      { oldValue }: AddCompareConditionValue,
      response: { vpAst: AstEventItem[] },
    ) => {
      const { sceneId } = oldValue
      const vpAst = (await response).vpAst
      const { sceneNode, noAstUpdate } = getUpdateAst(sceneId)
      store.dispatch('workspace/game/visualProgram/saveAST', {
        ast: vpAst,
        sceneNode,
        noAstUpdate,
      })
    },
    value,
    methodName: 'getAddCompareCondition',
  }
}

export function getEditCompareCondition(value: EditCompareConditionValue) {
  return {
    // eslint-disable-next-line
    execute: async ({ newValue }: EditCompareConditionValue) => {
      const { condition, eventId, compareIndex } = newValue
      store.commit('workspace/game/visualProgram/editConditionForCompare', {
        eventId,
        compareIndex,
        condition,
      })
      await store.dispatch('workspace/game/visualProgram/saveAST')
    },
    // eslint-disable-next-line
    undo: async ({ oldValue }: EditCompareConditionValue) => {
      const { condition, eventId, compareIndex } = oldValue
      store.commit('workspace/game/visualProgram/editConditionForCompare', {
        eventId,
        compareIndex,
        condition,
      })
      await store.dispatch('workspace/game/visualProgram/saveAST')
    },
    value,
    async: true,
    methodName: 'getEditCompareCondition',
  }
}

export function getDeleteCompareCondition(value: deleteCompareConditionValue) {
  return {
    // eslint-disable-next-line
    execute: async ({ newValue }: deleteCompareConditionValue) => {
      const { eventId, compareIndex, sceneId } = newValue
      const { sceneNode, noAstUpdate } = getUpdateAst(sceneId)
      const { ast, delCondition } = vpDeleteCompareItemByIndex({
        sceneNode,
        eventId,
        compareIndex,
      })
      await store.dispatch('workspace/game/visualProgram/saveAST', {
        ast,
        sceneNode,
        noAstUpdate,
      })
      return { delCondition }
    },
    // eslint-disable-next-line
    undo: async (
      { oldValue }: deleteCompareConditionValue,
      response: { delCondition: AstConditionItem },
    ) => {
      const { compareIndex, eventId, sceneId } = oldValue
      const { sceneNode, noAstUpdate } = getUpdateAst(sceneId)
      const delCondition = (await response).delCondition
      const vpAst = vpAddCompareConditionByIndex({
        sceneNode,
        compareIndex,
        eventId,
        condition: delCondition,
      })
      await store.dispatch('workspace/game/visualProgram/saveAST', {
        ast: vpAst,
        sceneNode,
        noAstUpdate,
      })
    },
    value,
    async: true,
    methodName: 'getDeleteCompareCondition',
  }
}

export function getDeleteCompareConditions(
  value: deleteCompareConditionsValue,
) {
  return {
    // eslint-disable-next-line
    execute: async ({ newValue }: deleteCompareConditionsValue) => {
      const { eventId, sceneId } = newValue
      const { sceneNode, noAstUpdate } = getUpdateAst(sceneId)
      const sceneAst = getVpAstFromSceneNode(sceneNode) as AstEventItem[]
      const oriAst = cloneDeep(sceneAst)
      const { ast } = vpDeleteCompareConditions({
        sceneNode,
        eventId,
      })
      await store.dispatch('workspace/game/visualProgram/saveAST', {
        ast,
        sceneNode,
        noAstUpdate,
      })
      return { oriAst }
    },
    // eslint-disable-next-line
    undo: async (
      { oldValue }: deleteCompareConditionsValue,
      response: { oriAst: AstEventItem[] },
    ) => {
      const { sceneId } = oldValue
      const { sceneNode, noAstUpdate } = getUpdateAst(sceneId)
      const vpAst = (await response).oriAst
      await store.dispatch('workspace/game/visualProgram/saveAST', {
        ast: vpAst,
        sceneNode,
        noAstUpdate,
      })
    },
    value,
    async: true,
    methodName: 'getDeleteCompareConditions',
  }
}

export function getEditConditionsForTypeChangeHistory(
  value: editConditionsForTypeChangeValue,
) {
  return {
    // eslint-disable-next-line
    execute: async ({ newValue }: editConditionsForTypeChangeValue) => {
      const { cmd, eventId, sceneId } = newValue
      const { sceneNode, noAstUpdate } = getUpdateAst(sceneId)
      const sceneAst = getVpAstFromSceneNode(sceneNode) as AstEventItem[]
      const oriAst = cloneDeep(sceneAst)
      const eventAst = getEventItemGroupById(sceneAst, eventId, true)
      const ansEventAstId = eventAst.map((item) => item.id)

      eventAst
        .filter(
          (k) =>
            k?.condition[0]?.content === 'VPDRAG_END' ||
            k?.condition[0]?.content === 'VPUNDRAG_END',
        )
        .map((k) => k?.condition[0]?.ext?.dragTargetArea)
        .forEach((v) => {
          if (v) {
            Object.values(v).forEach((uuids) =>
              uuids.forEach((uuid) => {
                const node = iframe.qcGame.nodePool.find(uuid)
                node && node.parent && node.parent.removeChild?.(node)
              }),
            )
          }
        })
      const dragEvents = sceneAst.filter(
        (item) =>
          isDragCondition(item?.condition[0]?.content) &&
          item.condition[0].node === eventAst[0]?.condition[0].node,
      )
      if (dragEvents.length === 1) {
        eventAst
          .filter((k) => k?.condition[0]?.content === 'VPDRAG_END')
          .map((k) => k?.condition[0]?.ext?.dragBound)
          .forEach((uuid) => {
            if (uuid) {
              const node = iframe.qcGame.nodePool.find(uuid)
              node && node.parent && node.parent.removeChild?.(node)
            }
          })
      }

      const node =
        store.state.workspace.game.activeNode ||
        store.state.workspace.game.sceneManager.currentSceneNode
      // 其他条件切换成拖拽类事件
      if (
        !isDragCondition(eventAst[0].condition[0].content) &&
        isDragCondition(cmd.content)
      ) {
        // 找到是否已有drag end事件,有则用现有的拖拽范围和拖拽方向数据
        const eventItem = sceneAst.find((ast) => {
          return (
            ast.condition[0] &&
            ast.condition[0].node === node.uuid &&
            isDragCondition(ast.condition[0].content)
          )
        })
        if (
          eventItem &&
          eventItem.condition[0] &&
          eventItem.condition[0].ext &&
          eventItem.condition[0].ext.dragBound
        ) {
          eventAst[0].condition[0].ext = {
            dragBound: eventItem.condition[0].ext.dragBound,
          }
        }
        if (
          eventItem &&
          eventItem.condition[0] &&
          eventItem.condition[0].dragDirection
        ) {
          eventAst[0].condition[0].dragDirection =
            eventItem.condition[0].dragDirection
        }
        if (
          eventItem &&
          eventItem.condition[0] &&
          typeof eventItem.condition[0].dragResetPos !== 'undefined'
        ) {
          eventAst[0].condition[0].dragResetPos =
            eventItem.condition[0].dragResetPos
        }
        if (
          eventAst[0].condition[0]?.content === 'VPDRAG_END' ||
          eventAst[0].condition[0]?.content === 'VPUNDRAG_END'
        ) {
          eventAst[0].condition[0].node2 = generateUUID()
        }
      }
      if (eventAst[0].condition[0].type === 'event' && cmd.type === 'event') {
        sceneAst.forEach((item) => {
          if (ansEventAstId.includes(item.id)) {
            // 带有条件参数的条件切换到其他没有带条件参数的条件时需要删除原来的条件参数
            if (
              isDragCondition(item.condition[0].content) &&
              !isDragCondition(cmd.content)
            ) {
              delete item.condition[0].dragDirection
              delete item.condition[0]?.dragResetPos
              delete item.condition[0].ext
              delete item.condition[0].node2
            }
            if (
              item.condition[0].content === DrawLineConditionType.DRAW_START &&
              cmd.content !== DrawLineConditionType.DRAW_START
            ) {
              delete item.condition[0].drawLineStartNode
            }
            if (
              item.condition[0].content === DrawLineConditionType.DRAW_END &&
              cmd.content !== DrawLineConditionType.DRAW_END
            ) {
              delete item.condition[0].isMatch
              delete item.condition[0].pairsUuid
              delete item.condition[0].pairCategory
            }
            if (
              (item.condition[0].content ===
                EraseConditionType.ERASE_REACH_STEP &&
                cmd.content !== EraseConditionType.ERASE_REACH_STEP) ||
              (item.condition[0].content ===
                EraseConditionType.ERASE_UN_REACH_STEP &&
                cmd.content !== EraseConditionType.ERASE_UN_REACH_STEP)
            ) {
              delete item.condition[0].erase
            }
            item.condition[0].content = cmd.content
            item.condition[0].conditionLabel = cmd.label
          }
        })
      } else if (
        eventAst[0].condition[0].type === 'compare' &&
        cmd.type === 'event'
      ) {
        const condition = {
          node: node.uuid,
          type: 'event',
          conditionLabel: cmd.label,
          content: cmd.content,
        }
        sceneAst.forEach((item) => {
          if (ansEventAstId.includes(item.id)) {
            item.condition.unshift(condition)
          }
        })
      } else if (
        eventAst[0].condition[0].type === 'event' &&
        cmd.type === 'compare'
      ) {
        if (eventAst[0].condition.length > 1) {
          sceneAst.forEach((item) => {
            if (ansEventAstId.includes(item.id)) {
              item.condition.splice(0, 1)
            }
          })
        } else {
          eventAst[0].condition[0].type = 'compare'
          eventAst[0].condition[0].conditionLabel = cmd.label
          delete eventAst[0].condition[0].content
        }
      }
      if (
        eventAst[0].condition[0].type === 'event' &&
        eventAst[0].condition[0].content === 'CUSTOM_EVENT'
      ) {
        eventAst[0].eventName = '自定义事件'
        eventAst[0].eventNameEn = 'Custom Event'
      } else {
        delete eventAst[0].eventName
        delete eventAst[0].eventNameEn
      }
      await store.dispatch('workspace/game/visualProgram/saveAST', {
        ast: sceneAst,
        sceneNode,
        noAstUpdate,
      })

      eventAst.forEach((item) => {
        if (
          item.condition[0] &&
          (item.condition[0].content === 'VPDRAG_END' ||
            item.condition[0].content === 'VPUNDRAG_END')
        ) {
          if (item.condition[0].ext && item.condition[0].ext.dragTargetArea) {
            for (const i in item.condition[0].ext.dragTargetArea) {
              if (item.condition[0].ext.dragTargetArea[i]) {
                const uuidsToDelete: string[] = []
                item.condition[0].ext.dragTargetArea[i].forEach((uuid) => {
                  const node = nodePool.find(uuid)
                  if (node) {
                    store.dispatch(
                      'workspace/game/recoverDragTargetAreaByUuid',
                      uuid,
                    )
                  } else {
                    uuidsToDelete.push(uuid)
                  }
                })
                uuidsToDelete.forEach((uuid) => {
                  item.condition[0].ext.dragTargetArea[i].splice(
                    item.condition[0].ext.dragTargetArea[i].indexOf(uuid),
                    1,
                  )
                })
                if (item.condition[0].ext.dragTargetArea[i].length === 0) {
                  delete item.condition[0].ext.dragTargetArea[i]
                }
              }
            }
          }
        }
        if (item.condition[0] && isDragCondition(item.condition[0].content)) {
          if (item.condition[0].ext && item.condition[0].ext.dragBound) {
            const uuid = item.condition[0].ext.dragBound
            const node = nodePool.find(uuid)
            const dragerNode = nodePool.find(item.condition[0].node)
            if (node) {
              store.dispatch('workspace/game/recoverDragBoundByUuid', {
                uuid,
                dragerNode,
              })
            } else {
              delete item.condition[0].ext.dragBound
            }
          }
        }
      })

      return { oriAst }
    },
    // eslint-disable-next-line
    undo: async (
      { oldValue }: editConditionsForTypeChangeValue,
      response: { oriAst: AstEventItem[] },
    ) => {
      const { sceneId } = oldValue
      const { sceneNode, noAstUpdate } = getUpdateAst(sceneId)
      const vpAst = await response.oriAst
      vpAst
        .filter(
          (k) =>
            k?.condition[0]?.content === 'VPDRAG_END' ||
            k?.condition[0]?.content === 'VPUNDRAG_END',
        )
        .map((k) => k?.condition[0]?.ext?.dragTargetArea)
        .forEach((v) => {
          if (v) {
            // 现在是普通，要undo到drag
            Object.values(v).forEach((uuids) =>
              uuids.forEach((uuid) => {
                store.dispatch(
                  'workspace/game/recoverDragTargetAreaByUuid',
                  uuid,
                )
              }),
            )
          }
        })
      vpAst.forEach((item) => {
        if (item.condition[0] && isDragCondition(item.condition[0].content)) {
          if (item.condition[0].ext && item.condition[0].ext.dragBound) {
            const uuid = item.condition[0].ext.dragBound
            const node = nodePool.find(uuid)
            const dragerNode = nodePool.find(item.condition[0].node)
            if (node) {
              store.dispatch('workspace/game/recoverDragBoundByUuid', {
                uuid,
                dragerNode,
              })
            }
          }
        }
      })
      await store.dispatch('workspace/game/visualProgram/saveAST', {
        ast: vpAst,
        sceneNode,
        noAstUpdate,
      })
    },
    value,
    async: true,
    methodName: 'getEditConditionsForTypeChangeHistory',
  }
}

export function getEditEventNameHistory(value: editEventNameValue) {
  return {
    // eslint-disable-next-line
    execute: async ({ newValue }: editEventNameValue) => {
      const { eventId, eventName } = newValue
      const sceneNode = store.state.workspace.game.sceneManager.currentSceneNode
      const sceneAst = getVpAstFromSceneNode(sceneNode) as AstEventItem[]
      const oriAst = cloneDeep(sceneAst)
      sceneAst.forEach((item) => {
        if (item.id === eventId) {
          const lang = language.get()
          if (lang === 'zh-cn') {
            item.eventName = eventName
          } else {
            item.eventNameEn = eventName
          }
        }
      })
      await store.dispatch('workspace/game/visualProgram/saveAST', {
        ast: sceneAst,
        sceneNode,
      })
      return { oriAst }
    },
    // eslint-disable-next-line
    undo: async (
      { oldValue }: editEventNameValue,
      response: { oriAst: AstEventItem[] },
    ) => {
      const sceneNode = store.state.workspace.game.sceneManager.currentSceneNode
      const vpAst = (await response).oriAst
      await store.dispatch('workspace/game/visualProgram/saveAST', {
        ast: vpAst,
        sceneNode,
      })
    },
    value,
    async: true,
    methodName: 'getEditEventNameHistory',
  }
}
type editEventValue = {
  newValue: {
    eventId: string
    node: string
    content: string
    conditionLabel: string
  }
  oldValue: {
    eventId: string
  }
}
export function getEditConditionHistory(value: editEventValue) {
  return {
    // eslint-disable-next-line
    execute: async ({ newValue }: editEventValue) => {
      const { eventId, node, content } = newValue
      const sceneNode = store.state.workspace.game.sceneManager.currentSceneNode
      const sceneAst = getVpAstFromSceneNode(sceneNode) as AstEventItem[]
      const oriAst = cloneDeep(sceneAst)
      sceneAst.forEach((ast: AstEventItem) => {
        if (ast.id === eventId && ast.condition.length > 0) {
          ast.condition[0].node = node
          ast.condition[0].content = content
          cookCondition(ast.condition[0])
        }
      })
      await store.dispatch('workspace/game/visualProgram/saveAST', {
        ast: sceneAst,
        sceneNode,
      })
      return { oriAst }
    },
    // eslint-disable-next-line
    undo: async (
      { oldValue }: editEventValue,
      response: { oriAst: AstEventItem[] },
    ) => {
      const sceneNode = store.state.workspace.game.sceneManager.currentSceneNode
      const vpAst = (await response).oriAst
      await store.dispatch('workspace/game/visualProgram/saveAST', {
        ast: vpAst,
        sceneNode,
      })
    },
    value,
    async: true,
    methodName: 'getEditConditionHistory',
  }
}

type sortCompareValue = {
  newValue: {
    eventId: string
    sortType: string
  }
  oldValue: {
    eventId: string
  }
}
export function getSortCompareConditionEventHistory(value: sortCompareValue) {
  return {
    // eslint-disable-next-line
    execute: async ({ newValue }: editEventValue) => {
      const { eventId, sortType } = newValue
      const sceneNode = store.state.workspace.game.sceneManager.currentSceneNode
      const sceneAst = getVpAstFromSceneNode(sceneNode) as AstEventItem[]
      const oriAst = cloneDeep(sceneAst)
      const index = sceneAst.findIndex((ast) => ast.id === eventId)
      let tempIndex = -1
      if (sortType === 'up') {
        for (let i = index - 1; i >= 0; i--) {
          if (
            sceneAst[i] &&
            sceneAst[i].condition &&
            sceneAst[i].condition.every((con) => con.type === 'compare')
          ) {
            tempIndex = i
            break
          }
        }
      } else if (sortType === 'down') {
        for (let i = index + 1; i < sceneAst.length; i++) {
          if (
            sceneAst[i] &&
            sceneAst[i].condition &&
            sceneAst[i].condition.every((con) => con.type === 'compare')
          ) {
            tempIndex = i
            break
          }
        }
      }
      if (tempIndex !== -1) {
        const temp = sceneAst[tempIndex]
        sceneAst[tempIndex] = sceneAst[index]
        sceneAst[index] = temp
      }
      await store.dispatch('workspace/game/visualProgram/saveAST', {
        ast: sceneAst,
        sceneNode,
      })
      return { oriAst }
    },
    // eslint-disable-next-line
    undo: async (
      { oldValue }: editEventValue,
      response: { oriAst: AstEventItem[] },
    ) => {
      const sceneNode = store.state.workspace.game.sceneManager.currentSceneNode
      const vpAst = (await response).oriAst
      await store.dispatch('workspace/game/visualProgram/saveAST', {
        ast: vpAst,
        sceneNode,
      })
    },
    value,
    async: true,
    methodName: 'getSortCompareConditionEventHistory',
  }
}
